<script setup lang="ts">
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, inject, reactive, ref, watch } from "vue";
import Button from "@/design/primitives/Button.vue";
import Field from "@/design/primitives/Field.vue";
import Pagination from "@/design/primitives/Pagination.vue";
import { DISPLAY_CONTEXT, type DisplayContext, SUBFORM_PORT } from "@/shared/contracts/ports";
import { type EntityDefinition, type FieldDefinition } from "@/shared/contracts/structure";
import { useTableLabels } from "@/shared/i18n/table-labels";
import { useAutofill } from "../autofill";
import { copySourcesOf, useCopyFrom } from "../copy-from";
import { resolveDefault } from "../default-token";
import { formatValue } from "../resolvers/field-config";
import { isComputedField, resolveWidget } from "../resolvers/field-widget";
import { itemNoun } from "../resolvers/item-noun";
import { recordFields, shownAtStage, shownWhen } from "../resolvers/record-fields";
import { useReferenceLabels } from "../resolvers/reference-labels";
import { say } from "../say";
import type { WidgetProps } from "./contract";
import { validate } from "./contract";

/**
 * A repeatable group — which is a CHILD ENTITY, not a json array (ADR-F4).
 *
 * That is the whole design decision made visible: every row here is a real
 * record with its own id, so "how many of product X did we sell" is a join, the
 * rows can move on a funnel, and an automation can fire on one. A `json[]` looks
 * identical on screen and can do none of it.
 *
 * New parents keep children locally until the native form saves the whole group.
 *
 * The add row is a REAL form over the child's own fields: each input is the same
 * widget the registry would resolve on the child's page, so a reference is a
 * searching select and a choice is a choice — never a bare text box asked to
 * hold a uuid.
 */
const props = withDefaults(
	defineProps<
		WidgetProps & {
			/** The child entity, from the relation. */
			childEntity: string;
			showTable?: boolean;
			draftMode?: boolean;
			childDefinition?: EntityDefinition;
			/** The FK column on the child. */
			parentField: string;
			parentId?: string | null;
			/**
			 * The entity the parent record BELONGS to — the children are read through
			 * it and the relation, not through the child's own list. Said by the
			 * caller: the link column's name says nothing about the parent.
			 */
			parentEntity: string;
			/**
			 * Which child columns to show, in order. Optional: absent, the child's own
			 * `showInList` fields decide — the same truth its list page reads.
			 */
			columns?: ReadonlyArray<{ code: string; label: string }>;
		}
	>(),
	// A tabela aparece a menos que alguém a desligue. Um boolean ausente o Vue
	// lê como `false`, e a gaveta (que não passa nada) escondia os itens que
	// acabara de gravar — o operador clicava de novo e duplicava (27/09).
	{ showTable: true },
);

const emit = defineEmits<{ open: [id: string]; changed: [] }>();
const cache = useQueryClient();
const subforms = inject(SUBFORM_PORT, null);
const rows = ref<Array<Record<string, unknown> & { id: string }>>([]);
/** How many children the parent has IN ALL — never `rows.length`. */
const total = ref(0);
const offset = ref(0);
const draft = reactive<Record<string, unknown>>({});
const touched = reactive<Record<string, boolean>>({});
/**
 * How a computed value is SHOWN — injected, as in `DynamicForm`, with the same
 * fallback for the rare mount outside the shell.
 */
const FALLBACK_DISPLAY: DisplayContext = {
	locale: "pt-BR",
	currency: "BRL",
	timeZone: "America/Sao_Paulo",
};
const display = inject(
	DISPLAY_CONTEXT,
	computed(() => FALLBACK_DISPLAY),
);
const busy = ref(false);
const failure = ref("");
/**
 * O formulário de um item NOVO começa fechado num registro que já existe (o painel
 * do cartão): aberto, o "Contato *" do filho parecia exigência do registro aberto.
 * O mesmo convite opcional do formulário de registro novo.
 */
const adding = ref(Boolean(props.draftMode));

const PAGE = 20;

const ready = computed(() => (Boolean(props.parentId) || props.draftMode) && subforms !== null);
const min = computed(() => Number(props.field.config.min ?? 0));
const max = computed(() => Number(props.field.config.max ?? Number.POSITIVE_INFINITY));

/**
 * The child's own definition, through the subform's own port — `dynamic/` never
 * learns a route. Without it the widget still works — text inputs and raw
 * values — which is the degraded mode this component shipped with, kept as the
 * fallback.
 */
const childFields = ref<FieldDefinition[]>([]);
const childEntityDef = ref<EntityDefinition | null>(null);
const childWritable = ref(false);
const childName = ref("");
watch(
	[() => props.childEntity, () => props.childDefinition],
	async ([code, definition], _, onCleanup) => {
		let current = true;
		onCleanup(() => {
			current = false;
		});
		childWritable.value = false;
		const child = definition ?? (code ? await subforms?.entityOf(code).catch(() => null) : null);
		if (!current) return;
		childName.value = child?.name ?? "";
		childEntityDef.value = child ?? null;
		childFields.value = child ? recordFields(child) : [];
		const relations = child ? await subforms?.relationsOf?.(code).catch(() => null) : [];
		if (!current) return;
		childFields.value = child ? recordFields(child, relations ?? []) : [];
		childWritable.value = Boolean(child) && child?.canWrite !== false && relations !== null;
	},
	{ immediate: true },
);
const childByCode = computed(() => new Map(childFields.value.map((f) => [f.code, f])));

/**
 * The inputs of the add row: every live child field someone can actually answer.
 * Computed values — a derived aggregate or a row formula — are the server's to
 * fill, and the FK to the parent is this widget's own job — offering either as
 * an input would be asking for a value nobody should type. The saved row still
 * SHOWS them (`columnsShown` keeps every listed column), which is where the
 * line's subtotal appears the moment the item lands.
 */
const inputFields = computed(() =>
	childFields.value.filter(
		(f) =>
			!isComputedField(f) &&
			f.code !== props.parentField &&
			f.code !== "stage" &&
			// A row added here is born at the child's first stage.
			(!childEntityDef.value ||
				(shownAtStage(f, childEntityDef.value, childEntityDef.value.process?.stages[0]?.code) &&
					shownWhen(f, childEntityDef.value, draft))),
	),
);

/**
 * A linha "adicionar" é o formulário de criação do filho com o vínculo ao pai já
 * escolhido: abre com o `config.default` de cada campo e com o que `copyFrom`
 * traz do pai (e de qualquer vínculo escolhido aqui) — o mesmo que o `DynamicForm`.
 */
const seeds = computed<Record<string, unknown>>(() => {
	const at = { now: new Date(), timeZone: display.value.timeZone, me: display.value.viewer };
	return Object.fromEntries(
		inputFields.value
			.map((f) => [f.code, resolveDefault(f.config?.default, at)] as const)
			.filter(([, value]) => value !== undefined),
	);
});
const round = ref(0);
function resetDraft(): void {
	for (const key of Object.keys(draft)) delete draft[key];
	for (const key of Object.keys(touched)) delete touched[key];
	Object.assign(draft, seeds.value);
	if (props.parentId) draft[props.parentField] = props.parentId;
	round.value++;
}
watch(
	[() => JSON.stringify(seeds.value), () => props.parentId, () => props.parentField],
	resetDraft,
	{
		immediate: true,
	},
);
/** Só o que a linha pergunta viaja — o que o autopreenchimento pôs fora dela é do servidor. */
const answered = () => Object.fromEntries(inputFields.value.map((f) => [f.code, draft[f.code]]));
const childOrEmpty = () =>
	childEntityDef.value ?? ({ code: props.childEntity, fields: [] } as unknown as EntityDefinition);
const copiedFrom = useCopyFrom({
	entity: childOrEmpty,
	values: draft,
	initial: () => undefined,
	round: () => round.value,
	factory: (code) => seeds.value[code],
});
useAutofill({ entity: childOrEmpty, values: draft, initial: () => undefined });

/**
 * Sem o pai gravado (rascunho), o que se copia DELE não tem de onde vir agora —
 * o trigger copia ao gravar o grupo. Cobrar esse campo vazio aqui travaria a linha.
 */
function demanded(field: FieldDefinition): boolean {
	return !(
		props.draftMode && copySourcesOf(field).some((source) => source.link === props.parentField)
	);
}
function problemOf(field: FieldDefinition): string | null {
	return demanded(field) ? validate(draft[field.code], field) : null;
}

/** What the table shows: the caller's choice, or the child's own list columns. */
const columnsShown = computed<ReadonlyArray<{ code: string; label: string }>>(() => {
	if (props.columns && props.columns.length > 0) return props.columns;
	const listed = childFields.value.filter((f) => f.showInList);
	const chosen = listed.length > 0 ? listed : childFields.value;
	return chosen.map((f) => ({ code: f.code, label: f.label }));
});

const pageQuery = useQuery({
	queryKey: computed(() => [
		"records",
		props.childEntity,
		"related",
		props.parentEntity,
		props.parentId,
		props.field.code,
		offset.value,
	]),
	enabled: computed(() => ready.value && !props.draftMode),
	queryFn: () => {
		if (!subforms || !props.parentId) throw new Error(say("subformLoadFailed"));
		return subforms.list({
			parentEntity: props.parentEntity,
			parentId: props.parentId,
			relation: props.field.code,
			limit: PAGE,
			offset: offset.value,
		});
	},
});
watch(
	pageQuery.data,
	(page) => {
		if (!page) return;
		rows.value = page.items;
		total.value = page.total;
		if (!page.items.length && offset.value > 0) offset.value = Math.max(0, offset.value - PAGE);
	},
	{ immediate: true },
);
watch(pageQuery.error, (error) => {
	if (error) failure.value = say("subformLoadFailed");
});
watch(
	[() => props.parentId, () => props.field.code, () => props.childEntity, () => props.parentEntity],
	() => {
		offset.value = 0;
		if (props.draftMode) {
			rows.value = [];
			total.value = 0;
			resetDraft();
			failure.value = "";
		}
	},
);
async function reload() {
	// Todo registro, não só os filhos: gravar um filho muda o pai (saldo) e o que ele
	// aponta, e é desse cache que o copyFrom da próxima linha lê.
	await cache.invalidateQueries({ queryKey: ["records"] });
	await cache.invalidateQueries({ queryKey: ["board"] });
	emit("changed");
}

function problemFor(field: FieldDefinition): string {
	if (!touched[field.code]) return "";
	return problemOf(field) ?? "";
}

async function add(): Promise<void> {
	if (!subforms || (!props.parentId && !props.draftMode) || props.disabled || !childWritable.value)
		return;
	if (total.value >= max.value) {
		failure.value = `no máximo ${max.value} itens`;
		return;
	}
	// Validated with the child's OWN rules before the round trip, so "picolé é
	// obrigatório" appears under the picolé box, not as a server refusal below
	// the table.
	for (const field of inputFields.value) touched[field.code] = true;
	if (inputFields.value.some((f) => problemOf(f) !== null)) return;

	if (props.draftMode) {
		stageDraft();
		return;
	}
	busy.value = true;
	failure.value = "";
	try {
		await subforms.create({
			entity: props.childEntity,
			values: { ...answered(), [props.parentField]: props.parentId },
		});
		// Relê ANTES de recomeçar: a próxima linha copia o saldo de agora, não o da abertura.
		await reload();
		resetDraft();
	} catch (e) {
		failure.value = e instanceof Error ? e.message : say("subformAddFailed");
	} finally {
		busy.value = false;
	}
}

async function remove(id: string): Promise<void> {
	if (props.disabled || busy.value) return;
	if (props.draftMode) {
		rows.value = rows.value.filter((row) => row.id !== id);
		total.value = rows.value.length;
		return;
	}
	if (!subforms) return;
	try {
		await subforms.remove({ entity: props.childEntity, id });
		await reload();
	} catch (error) {
		failure.value = error instanceof Error ? error.message : say("subformAddFailed");
	}
}

// O que só veio de fábrica (o padrão) não é rascunho de ninguém.
const hasDraft = computed(() =>
	Object.entries(draft).some(
		([code, value]) =>
			value !== undefined &&
			value !== null &&
			value !== "" &&
			(!Array.isArray(value) || value.length > 0) &&
			JSON.stringify(value) !== JSON.stringify(seeds.value[code]),
	),
);
const dirty = computed(() => Boolean(props.draftMode) && (rows.value.length > 0 || hasDraft.value));
function stageDraft(): boolean {
	if (total.value >= max.value) {
		failure.value = `no máximo ${max.value} itens`;
		return false;
	}
	for (const field of inputFields.value) touched[field.code] = true;
	if (!childWritable.value || inputFields.value.some((field) => problemOf(field) !== null))
		return false;
	rows.value.push({ ...answered(), id: crypto.randomUUID() });
	total.value = rows.value.length;
	resetDraft();
	failure.value = "";
	return true;
}
function editDraft(id: string): void {
	if (props.disabled || busy.value || (hasDraft.value && !stageDraft())) return;
	const row = rows.value.find((item) => item.id === id);
	if (!row) return;
	const { id: _, ...values } = row;
	rows.value = rows.value.filter((item) => item.id !== id);
	total.value = rows.value.length;
	Object.assign(draft, values);
}
function collect() {
	if (!props.draftMode) return [];
	if (hasDraft.value && !stageDraft()) return null;
	if (total.value < min.value) return null;
	return rows.value.map(({ id: _, ...values }) => ({
		relation: props.field.code,
		entity: props.childEntity,
		values,
	}));
}
defineExpose({ collect, dirty });

// O botão nomeia UM item da tabela filha ("agendamento"), não o campo que aponta para cá.
const addName = computed(() =>
	childName.value ? itemNoun(childName.value, display.value.locale) : props.field.label,
);

// Names for the reference cells, not uuids — the same resolver the tables use.
const labels = useReferenceLabels(childFields, rows);

function cell(row: Record<string, unknown>, code: string): string {
	const field = childByCode.value.get(code);
	const value = field
		? formatValue(row[code], field.type, display.value, field.config, labels.value)
		: row[code];
	return value === null || value === undefined || value === "" ? "—" : String(value);
}
const { pagination } = useTableLabels();
</script>

<template>
	<div class="min-w-0 space-y-2">
		<p v-if="!ready" class="rounded-xl border border-dashed border-border px-3 py-3 text-xs text-fg-subtle">
			Salve o registro primeiro — os itens precisam de algo a que pertencer.
		</p>

		<template v-else>
			<div v-if="showTable !== false" class="overflow-x-auto rounded-xl border border-border">
				<table class="w-full text-sm">
					<thead class="border-b border-border text-xs text-fg-subtle">
						<tr>
							<!-- Uma linha só, e a tabela rola de lado: espremida num painel estreito
							     ela partia "ECOCARDIOGRAMA" e o cabeçalho virava cinco linhas. -->
							<th v-for="column in columnsShown" :key="column.code" class="max-w-48 truncate whitespace-nowrap px-3 py-2 text-left font-medium" :title="column.label">
								{{ column.label }}
							</th>
							<th class="w-10" />
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in rows" :key="row.id" class="border-t border-border/60 first:border-t-0">
							<td v-for="column in columnsShown" :key="column.code" class="whitespace-nowrap px-3 py-2 text-fg">
								{{ cell(row, column.code) }}
							</td>
							<td class="whitespace-nowrap px-2 py-2 text-right">
								<button type="button" :disabled="disabled || busy" class="mr-3 text-xs text-accent underline" @click="draftMode ? editDraft(row.id) : emit('open', row.id)">abrir / editar</button>
								<button
									type="button"
									class="text-xs text-danger underline"
									:disabled="disabled || total <= min"
									@click="remove(row.id)"
								>
									remover
								</button>
							</td>
						</tr>
						<tr v-if="total === 0" class="border-t border-border/60">
							<td :colspan="columnsShown.length + 1" class="px-3 py-3 text-xs text-fg-subtle">
								Nenhum item ainda.
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<Pagination
				v-if="showTable !== false && !draftMode"
				:labels="pagination"
				:total="total"
				:limit="PAGE"
				:offset="offset"
				@update="offset = $event"
			/>

			<button
				v-if="!adding"
				type="button"
				class="text-xs font-medium text-accent hover:underline"
				:disabled="disabled || !childWritable"
				@click="adding = true"
			>
				{{ say("addChildren", { name: addName }) }}
			</button>
			<div v-else class="grid grid-cols-[repeat(auto-fit,minmax(min(100%,16rem),1fr))] items-start gap-4 pt-2">
				<Field
					v-for="input in inputFields"
					:key="input.code"
					v-slot="{ id, describedBy }"
					:label="input.label"
					:required="input.required && demanded(input)"
					:hint="copiedFrom[input.code] ? say('copiedFrom', { link: copiedFrom[input.code] }) : undefined"
					:error="problemFor(input)"
					class="min-w-0"
					:class="{ 'col-span-full': input.type === 'long_text' }"
				>
					<component
						:is="resolveWidget(input)"
						:field="input"
						:model-value="draft[input.code]"
						:disabled="disabled || busy || !childWritable"
						:error="problemFor(input)"
						:control-id="id"
						:described-by="describedBy"
						@update:model-value="draft[input.code] = $event"
						@blur="touched[input.code] = true"
					/>
				</Field>
				<Button class="col-span-full justify-self-end" variant="secondary" size="sm" :disabled="disabled || busy || !childWritable" @click="add">
					adicionar
				</Button>
			</div>

			<p v-if="total < min" class="text-xs text-danger">
				Adicione pelo menos {{ min }} item(ns).
			</p>
			<p v-if="failure" class="text-xs text-danger">{{ failure }}</p>
		</template>
	</div>
</template>
